<template>
  <div class="bg-white rounded-lg border border-gray-200 shadow-sm">
    <div class="px-4 py-3 border-b border-gray-200">
      <h2 class="heading-section">New Call</h2>
    </div>
    <div class="p-4">
      <div class="space-y-4">
        <div class="flex gap-4 items-end">
          <div class="flex-1 max-w-md">
            <label for="config-select" class="block text-sm font-medium text-gray-700 mb-1">Configuration</label>
            <select id="config-select" v-model="selectedConfigId" class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-dark focus:border-blue-dark">
              <option value="">Select configuration...</option>
              <option
                v-for="config in configurations"
                :key="String(config.id)"
                :value="String(config.id)"
              >
                {{ config.name }}
              </option>
            </select>
          </div>
          <div class="w-64">
            <label for="phone-input" class="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
            <input
              id="phone-input"
              v-model="phoneNumber"
              type="tel"
              pattern="[\d\s\-\+\(\)\.ext]+"
              placeholder="Enter phone number"
              class="sensitive w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-offset-0 focus:border-blue-dark focus:ring-blue-dark"
            />
          </div>
          <button @click="handleStartCall" :disabled="!canStartCall || isLoading" class="btn-primary-sm">
            {{ isLoading ? 'Starting...' : 'Start Call' }}
          </button>
          <div class="flex items-end pb-2">
            <label class="flex items-center cursor-pointer gap-2">
              <div class="relative flex items-center">
                <input
                  type="checkbox"
                  v-model="saveCallRecord"
                  class="peer shrink-0 appearance-none w-3.5 h-3.5 border-2 border-gray-300 rounded bg-white checked:bg-primary-500 checked:border-orange-500 focus:outline-none focus:ring-2 focus:ring-blue-dark focus:ring-offset-0"
                />
                <font-awesome-icon
                  icon="check"
                  class="absolute w-4 h-4 text-xs hidden peer-checked:block pointer-events-none text-white"
                />
              </div>
              <span class="text-sm text-gray-700">Save call record</span>
            </label>
          </div>
        </div>

        <!-- Data Elements -->
        <div v-if="selectedConfig && hasDataElements" class="border-t pt-4">
          <h3 class="text-sm font-medium text-gray-700 mb-3">Call Data</h3>
          <div class="grid grid-cols-3 gap-4">
            <!-- Required Elements -->
            <div
              v-for="elem in requiredElements"
              :key="'req-' + elem"
              class="col-span-1"
            >
              <label class="block text-sm font-medium text-gray-700 mb-1">
                {{ elem }} <span class="text-red-500">*</span>
              </label>
              <input
                v-model="metadata[elem]"
                type="text"
                :placeholder="elem"
                class="sensitive w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-offset-0 focus:border-blue-dark focus:ring-blue-dark"
              />
            </div>
            <!-- Optional Elements -->
            <div
              v-for="elem in optionalElements"
              :key="'opt-' + elem"
              class="col-span-1"
            >
              <label class="block text-sm font-medium text-gray-700 mb-1">{{ elem }}</label>
              <input
                v-model="metadata[elem]"
                type="text"
                :placeholder="elem"
                class="sensitive w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-offset-0 focus:border-blue-dark focus:ring-blue-dark"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { useToast } from 'vue-toastification'

export default {
  name: 'CallCreateForm',
  props: {
    configurations: {
      type: Array,
      required: true,
    },
  },
  emits: ['call-started'],
  data() {
    return {
      phoneNumber: '',
      selectedConfigId: '',
      isLoading: false,
      saveCallRecord: true,
      metadata: {},
    }
  },
  computed: {
    toast() {
      return useToast()
    },
    selectedConfig() {
      if (!this.selectedConfigId) return null
      return this.configurations.find(c => String(c.id) === String(this.selectedConfigId))
    },
    requiredElements() {
      return this.selectedConfig?.data_elements?.required || []
    },
    optionalElements() {
      return this.selectedConfig?.data_elements?.optional || []
    },
    hasDataElements() {
      return this.requiredElements.length > 0 || this.optionalElements.length > 0
    },
    canStartCall() {
      if (!this.phoneNumber || !this.selectedConfigId) return false

      // Check if all required data elements are filled
      return this.requiredElements.every(elem => this.metadata[elem]?.trim())
    },
  },
  watch: {
    selectedConfigId() {
      // Reset metadata when configuration changes
      this.metadata = {}
    },
  },
  methods: {
    async handleStartCall() {
      if (!this.canStartCall) return

      this.isLoading = true
      try {
        // Build metadata with only non-empty values
        const cleanMetadata = {}
        Object.keys(this.metadata).forEach(key => {
          if (this.metadata[key]?.trim()) {
            cleanMetadata[key] = this.metadata[key].trim()
          }
        })

        const response = await axios.post('api/outreach/calls/start/', {
          to_phone_number: this.phoneNumber,
          call_configuration_id: this.selectedConfigId,
          save_call_record: this.saveCallRecord,
          metadata: cleanMetadata
        })

        this.toast.success('Call started successfully!')

        this.phoneNumber = ''
        this.selectedConfigId = ''
        this.metadata = {}

        this.$emit('call-started', response.data)
      } catch (error) {
        console.error('Failed to start call:', error)
        const errorMessage = error.response?.data?.error || 'Failed to start call. Please try again.'
        this.toast.error(errorMessage)
      } finally {
        this.isLoading = false
      }
    },
  },
}
</script>
